import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Logo from '../components/Logo';
import { getAssessmentResults } from '../utils/api';
import { personalityContent, discModelExplanation, graphScaleLabels, getContentForStyle } from '../utils/personalityContent';
import { Printer, AlertTriangle } from 'lucide-react';

// Professional Graph Component with scale labels
const ProfessionalGraph = ({ scores, title, subtitle }) => {
  const maxScore = 100;
  const containerHeight = 180;
  const barWidth = 36;

  const colors = {
    D: '#DC2626',
    I: '#F58820',
    S: '#2563EB',
    C: '#EAB308',
  };

  const bars = [
    { key: 'D', value: scores?.D || 0 },
    { key: 'I', value: scores?.I || 0 },
    { key: 'S', value: scores?.S || 0 },
    { key: 'C', value: scores?.C || 0 },
  ];

  // Simplified scale labels for compact display
  const compactLabels = [
    { value: 100, label: '100' },
    { value: 80, label: '80' },
    { value: 60, label: '60' },
    { value: 50, label: '50' },
    { value: 40, label: '40' },
    { value: 20, label: '20' },
    { value: 0, label: '0' },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 min-w-0">
      <div className="text-center mb-2">
        <h4 className="font-bold text-gray-800 text-sm">{title}</h4>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>

      <div className="flex">
        {/* Scale labels - compact */}
        <div className="flex flex-col justify-between text-xs text-gray-500 pr-1 flex-shrink-0" style={{ height: containerHeight, width: '24px' }}>
          {compactLabels.map((item) => (
            <span key={item.value} className="text-right text-[10px]">{item.label}</span>
          ))}
        </div>

        {/* Graph area */}
        <div className="flex-1 min-w-0">
          <div
            className="flex items-end justify-around border-l-2 border-b-2 border-gray-300 pl-1 pb-1 relative"
            style={{ height: containerHeight }}
          >
            {/* Horizontal grid lines */}
            {[80, 60, 50, 40, 20].map((val) => (
              <div
                key={val}
                className="absolute left-0 right-0 border-t border-gray-200"
                style={{ bottom: `${(val / maxScore) * 100}%` }}
              />
            ))}

            {/* Bars */}
            {bars.map((bar) => {
              const barHeight = (bar.value / maxScore) * containerHeight;
              return (
                <div key={bar.key} className="flex flex-col items-center justify-end h-full z-10">
                  <div
                    className="rounded-t-sm transition-all duration-500 relative"
                    style={{
                      width: barWidth,
                      height: Math.max(barHeight, 4),
                      backgroundColor: colors[bar.key],
                    }}
                  >
                    {/* Value label on bar */}
                    <span className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-[10px] font-bold text-gray-700">
                      {bar.value}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* X-axis labels */}
          <div className="flex justify-around mt-1 pl-1">
            {bars.map((bar) => (
              <div key={bar.key} className="text-center" style={{ width: barWidth }}>
                <span className="font-bold text-xs" style={{ color: colors[bar.key] }}>
                  {bar.key}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// DISC Circle Graph Component
const DISCCircleGraph = ({ primaryStyle, blend }) => {
  const isLevel = blend === 'LEVEL';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 min-w-0">
      <h4 className="font-bold text-gray-800 text-center mb-2 text-sm">DISC Quadrant</h4>
      <div className="flex justify-center">
        <svg width="160" height="160" viewBox="0 0 200 200" className="max-w-full">
          {/* Background circle */}
          <circle cx="100" cy="100" r="90" fill="#f3f4f6" stroke="#d1d5db" strokeWidth="2" />

          {/* Quadrant lines */}
          <line x1="100" y1="10" x2="100" y2="190" stroke="#d1d5db" strokeWidth="1" />
          <line x1="10" y1="100" x2="190" y2="100" stroke="#d1d5db" strokeWidth="1" />

          {/* Quadrant labels */}
          <text x="50" y="50" textAnchor="middle" className="text-lg font-bold" fill="#DC2626">D</text>
          <text x="150" y="50" textAnchor="middle" className="text-lg font-bold" fill="#F58820">I</text>
          <text x="150" y="160" textAnchor="middle" className="text-lg font-bold" fill="#2563EB">S</text>
          <text x="50" y="160" textAnchor="middle" className="text-lg font-bold" fill="#EAB308">C</text>

          {/* Axis labels */}
          <text x="100" y="8" textAnchor="middle" fontSize="10" fill="#666">OUTGOING</text>
          <text x="100" y="198" textAnchor="middle" fontSize="10" fill="#666">RESERVED</text>
          <text x="5" y="100" textAnchor="start" fontSize="10" fill="#666" transform="rotate(-90 12 100)">TASK</text>
          <text x="195" y="100" textAnchor="end" fontSize="10" fill="#666" transform="rotate(90 188 100)">PEOPLE</text>

          {/* Position indicator */}
          {isLevel ? (
            <circle cx="100" cy="100" r="15" fill="#6B7280" stroke="#fff" strokeWidth="3" />
          ) : (
            <circle
              cx={primaryStyle === 'D' ? 55 : primaryStyle === 'I' ? 145 : primaryStyle === 'S' ? 145 : 55}
              cy={primaryStyle === 'D' ? 55 : primaryStyle === 'I' ? 55 : primaryStyle === 'S' ? 145 : 145}
              r="15"
              fill={
                primaryStyle === 'D' ? '#DC2626' :
                primaryStyle === 'I' ? '#F58820' :
                primaryStyle === 'S' ? '#2563EB' : '#EAB308'
              }
              stroke="#fff"
              strokeWidth="3"
            />
          )}
        </svg>
      </div>
      <p className="text-center text-sm text-gray-600 mt-2">
        {isLevel ? 'Balanced/Level Profile' : `Primary: ${personalityContent[primaryStyle]?.name || primaryStyle}`}
      </p>
    </div>
  );
};

// Page Header Component
const PageHeader = ({ assessmentData, pageNumber, totalPages, accessCode }) => (
  <div className="flex justify-between items-start border-b border-gray-300 pb-4 mb-6">
    <div className="flex items-center gap-4">
      <Logo size="small" />
      <div>
        <h1 className="text-lg font-bold text-gray-800">TSVMap Personality Profile</h1>
        <p className="text-sm text-gray-600">
          {assessmentData?.respondent_first_name} {assessmentData?.respondent_last_name}
        </p>
      </div>
    </div>
    <div className="text-right text-sm text-gray-600">
      {accessCode && <p>Access Code: {accessCode}</p>}
      <p>Page {pageNumber} of {totalPages}</p>
    </div>
  </div>
);

// Page Footer Component
const PageFooter = () => (
  <div className="border-t border-gray-300 pt-4 mt-6 text-center text-xs text-gray-500">
    <p>© {new Date().getFullYear()} TSVMap. All rights reserved.</p>
    <p>www.tsvmap.com</p>
  </div>
);

const ProfessionalReport = () => {
  const { uuid } = useParams();
  const location = useLocation();
  const [results, setResults] = useState(location.state?.results || null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [loading, setLoading] = useState(!location.state?.results);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!results) {
      loadResults();
    }
  }, [uuid]);

  const loadResults = async () => {
    try {
      const response = await getAssessmentResults(uuid);
      setAssessmentData(response.data);
      setResults({
        environmentalStyle: {
          D: response.data.env_d_score,
          I: response.data.env_i_score,
          S: response.data.env_s_score,
          C: response.data.env_c_score,
        },
        basicStyle: {
          D: response.data.basic_d_score,
          I: response.data.basic_i_score,
          S: response.data.basic_s_score,
          C: response.data.basic_c_score,
        },
        personalityStyle: {
          primary: response.data.primary_style,
          secondary: response.data.secondary_style,
          blend: response.data.style_blend,
        },
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Generate access code from UUID
  const generateAccessCode = (uuid) => {
    if (!uuid) return '';
    const clean = uuid.replace(/-/g, '').toUpperCase();
    return `${clean.slice(0, 4)}-${clean.slice(4, 8)}-${clean.slice(8, 12)}-${clean.slice(12, 16)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tsvmap-orange" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Results</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const { environmentalStyle, basicStyle, personalityStyle } = results || {};
  const content = getContentForStyle(personalityStyle?.primary, personalityStyle?.blend);
  const accessCode = generateAccessCode(uuid);
  const completionDate = assessmentData?.completed_at
    ? new Date(assessmentData.completed_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const totalPages = 5;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Print Button Header - Hidden in Print */}
      <header style={{ backgroundColor: '#0B2134' }} className="shadow-sm no-print">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Logo size="small" />
          <button
            onClick={handlePrint}
            className="flex items-center px-4 py-2 text-white border border-white/30 rounded-lg hover:bg-white/10"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print Report
          </button>
        </div>
      </header>

      {/* Report Content */}
      <div className="max-w-4xl mx-auto p-4">
        {/* ==================== PAGE 1: Cover & Overview ==================== */}
        <div className="bg-white shadow-lg mb-8 print-page">
          <div className="p-8">
            {/* Header Info */}
            <div className="flex justify-between items-start mb-8">
              <div>
                <Logo size="large" />
              </div>
              <div className="text-right text-sm text-gray-600">
                <p className="font-semibold">Prepared by:</p>
                <p>TSVMap Assessment Platform</p>
                <p>www.tsvmap.com</p>
              </div>
            </div>

            {/* Title Section */}
            <div className="text-center py-8 border-y border-gray-200 mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Personality Profile Report</h1>
              <p className="text-xl text-gray-600">
                {assessmentData?.respondent_first_name} {assessmentData?.respondent_last_name}
              </p>
              <div className="mt-4 text-sm text-gray-500">
                <p>Access Code: {accessCode}</p>
                <p>Completed: {completionDate}</p>
              </div>
            </div>

            {/* Primary Style Display */}
            <div className="flex items-center justify-center gap-8 mb-8">
              <div
                className="w-32 h-32 rounded-full flex items-center justify-center text-white text-5xl font-bold shadow-lg"
                style={{ backgroundColor: content.color }}
              >
                {content.letter}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{content.name}</h2>
                <p className="text-lg text-gray-600">{content.subtitle}</p>
                <div className="flex gap-2 mt-2">
                  {content.characteristics.map((char) => (
                    <span key={char} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                      {char}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Overview */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="font-bold text-gray-800 mb-3">Overview</h3>
              <p className="text-gray-700 leading-relaxed">{content.overview}</p>
            </div>

            {/* Quick Score Display */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {['D', 'I', 'S', 'C'].map((style) => (
                <div
                  key={style}
                  className="text-center p-4 rounded-lg"
                  style={{ backgroundColor: `${personalityContent[style].color}15` }}
                >
                  <div className="text-3xl font-bold" style={{ color: personalityContent[style].color }}>
                    {basicStyle?.[style] || 0}
                  </div>
                  <div className="text-sm text-gray-600">{personalityContent[style].name}</div>
                </div>
              ))}
            </div>

            <PageFooter />
          </div>
        </div>

        {/* ==================== PAGE 2: Words & Strengths ==================== */}
        <div className="bg-white shadow-lg mb-8 print-page">
          <div className="p-8">
            <PageHeader
              assessmentData={assessmentData}
              pageNumber={2}
              totalPages={totalPages}
              accessCode={accessCode}
            />

            {/* Words That Describe You */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Words That Describe You
              </h3>
              <p className="text-gray-600 mb-4">
                The following words describe general behavioral tendencies. Not all words will describe you equally well, but overall they paint a picture of your natural style.
              </p>
              <div className="flex flex-wrap gap-2">
                {content.descriptiveWords.map((word) => (
                  <span
                    key={word}
                    className="px-4 py-2 rounded-lg text-sm font-medium"
                    style={{ backgroundColor: `${content.color}20`, color: content.color }}
                  >
                    {word}
                  </span>
                ))}
              </div>
            </div>

            {/* Strengths */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Your Strengths
              </h3>
              <div className="space-y-4">
                {content.strengthsParagraphs.map((paragraph, idx) => (
                  <p key={idx} className="text-gray-700 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            <PageFooter />
          </div>
        </div>

        {/* ==================== PAGE 3: Keys to Excellence ==================== */}
        <div className="bg-white shadow-lg mb-8 print-page">
          <div className="p-8">
            <PageHeader
              assessmentData={assessmentData}
              pageNumber={3}
              totalPages={totalPages}
              accessCode={accessCode}
            />

            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Keys to Excellence
              </h3>
              <p className="text-gray-600 mb-6">
                These keys are important in adapting to the needs and perspectives of others. Focusing on these areas will help you communicate more effectively and build stronger relationships.
              </p>
              <div className="space-y-4">
                {content.keysToExcellence.map((key, idx) => (
                  <div key={idx} className="flex items-start gap-4 bg-gray-50 rounded-lg p-4">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                      style={{ backgroundColor: content.color }}
                    >
                      {idx + 1}
                    </div>
                    <p className="text-gray-700 leading-relaxed">{key}</p>
                  </div>
                ))}
              </div>
            </div>

            <PageFooter />
          </div>
        </div>

        {/* ==================== PAGE 4: Team Member ==================== */}
        <div className="bg-white shadow-lg mb-8 print-page">
          <div className="p-8">
            <PageHeader
              assessmentData={assessmentData}
              pageNumber={4}
              totalPages={totalPages}
              accessCode={accessCode}
            />

            <h3 className="text-xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-200">
              As a Team Member
            </h3>

            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-800 mb-2">Main Strength</h4>
                  <p className="text-gray-700">{content.teamMember.mainStrength}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-bold text-gray-800 mb-2">Main Motivation</h4>
                  <p className="text-gray-700">{content.teamMember.mainMotivation}</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-bold text-gray-800 mb-2">Talents</h4>
                <p className="text-gray-700">{content.teamMember.talents}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-bold text-gray-800 mb-2">Value to Team</h4>
                <p className="text-gray-700">{content.teamMember.valueToTeam}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-bold text-gray-800 mb-2">Ideal Environment</h4>
                <p className="text-gray-700">{content.teamMember.idealEnvironment}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-bold text-gray-800 mb-2">Keys to Motivate</h4>
                <p className="text-gray-700">{content.teamMember.keysToMotivate}</p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-bold text-yellow-800 mb-2">Possible Blind Spots</h4>
                <p className="text-yellow-700">{content.teamMember.possibleBlindSpots}</p>
              </div>
            </div>

            <PageFooter />
          </div>
        </div>

        {/* ==================== PAGE 5: Graphs & DISC Model ==================== */}
        <div className="bg-white shadow-lg mb-8 print-page">
          <div className="p-8">
            <PageHeader
              assessmentData={assessmentData}
              pageNumber={5}
              totalPages={totalPages}
              accessCode={accessCode}
            />

            <h3 className="text-xl font-bold text-gray-900 mb-6 pb-2 border-b border-gray-200">
              Personality Graphs
            </h3>

            {/* Graphs */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <ProfessionalGraph
                scores={environmentalStyle}
                title="Graph I"
                subtitle="Environmental Style"
              />
              <ProfessionalGraph
                scores={basicStyle}
                title="Graph II"
                subtitle="Basic Style"
              />
              <DISCCircleGraph
                primaryStyle={personalityStyle?.primary}
                blend={personalityStyle?.blend}
              />
            </div>

            {/* Graph Explanation */}
            <div className="bg-gray-50 rounded-lg p-4 mb-8">
              <h4 className="font-bold text-gray-800 mb-2">Understanding Your Graphs</h4>
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
                <div>
                  <p className="font-semibold">Graph I - Environmental Style:</p>
                  <p>How you tend to respond to your environment and how others perceive you.</p>
                </div>
                <div>
                  <p className="font-semibold">Graph II - Basic Style:</p>
                  <p>Your natural, instinctive behavior pattern - who you are at your core.</p>
                </div>
              </div>
            </div>

            {/* DISC Model Explanation */}
            <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              {discModelExplanation.title}
            </h3>
            <p className="text-gray-700 mb-6">{discModelExplanation.intro}</p>

            <div className="grid grid-cols-2 gap-4">
              {Object.entries(discModelExplanation.styles).map(([key, style]) => (
                <div
                  key={key}
                  className="p-4 rounded-lg border-2"
                  style={{ borderColor: personalityContent[key].color, backgroundColor: `${personalityContent[key].color}10` }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: personalityContent[key].color }}
                    >
                      {key}
                    </div>
                    <span className="font-bold" style={{ color: personalityContent[key].color }}>
                      {style.name}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{style.description}</p>
                </div>
              ))}
            </div>

            <p className="text-gray-600 mt-6 text-sm">{discModelExplanation.understanding}</p>

            <PageFooter />
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .print-page {
            page-break-after: always;
            page-break-inside: avoid;
            margin: 0;
            box-shadow: none;
          }
          .print-page:last-child {
            page-break-after: auto;
          }
          body {
            background: white !important;
          }
          .bg-gray-100 {
            background: white !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfessionalReport;
